/**
 * API response-projection contracts: the shapes the API *returns* and the
 * non-entity request bodies it accepts, as distinct from the entity and
 * create/update schemas in `entities.ts`. Both apps/api and apps/web import
 * these from `@paradise/shared` and never a subpath.
 *
 * Types are derived via `z.infer` — never hand-write a type that duplicates a
 * schema. See docs/plan.md for the endpoints these mirror.
 */
import { z } from 'zod';
import { Id, Product, ProductPhoto, Lead, LeadItem, LeadEvent } from './entities.js';
import { LeadStage } from './enums.js';
// ---------------------------------------------------------------------------
// Catalogue
// ---------------------------------------------------------------------------
/**
 * One file in the eager-precache manifest. The pipeline writes TWO files per
 * photo — an ~800px `cover` and a ~240px `thumb` — so the manifest is a flat
 * list of files, not of photos, and cache completeness is
 * `cache.keys().length === assets.length`. See docs/plan.md, Eager catalogue
 * precache.
 */
export const ManifestAsset = z.object({
    path: z.string().min(1),
    bytes: z.number().int().nonnegative(),
    kind: z.enum(['thumb', 'cover']),
    itemNo: z.number().int().positive()
});
export const CatalogueManifest = z.object({
    // A content/version marker so a changed catalogue is a new manifest; the
    // precache diff is a set difference over `assets[].path` regardless.
    version: z.string().min(1),
    assets: z.array(ManifestAsset)
});
/**
 * A product as the catalogue text endpoint serves it: the row plus its ordered
 * gallery (sortOrder 0 is the cover). Category and material NAMES are resolved
 * client-side by joining against the `/categories` and `/materials` lists the
 * client also fetches — the product stays id-only on the wire.
 */
export const ProductWithPhotos = Product.extend({
    photos: z.array(ProductPhoto)
});
// ---------------------------------------------------------------------------
// Leads
// ---------------------------------------------------------------------------
/**
 * A lead as the board/list endpoint serves it: the row plus its quoted lines,
 * so the board can render item counts and totals and the detail view works
 * offline from the mirror. Excludes archived leads at the query, not the schema.
 */
export const LeadWithItems = Lead.extend({
    items: z.array(LeadItem)
});
/**
 * A lead as the detail endpoint serves it: with items and the full
 * stage-history timeline. Newest-first is a rendering concern, not enforced
 * here.
 */
export const LeadDetail = LeadWithItems.extend({
    history: z.array(LeadEvent)
});
/**
 * The one-field stage move that also appends a `LeadEvent`. Requires
 * connectivity (see docs/plan.md — it is disabled offline). Tapping the
 * already-active stage is a client-side no-op, so the server need not special
 * case it.
 */
export const StageChangeInput = z.object({
    stage: LeadStage,
    note: z.string().nullable().optional()
});
// ---------------------------------------------------------------------------
// Buyer directory — deduplicated projection over ALL leads
// ---------------------------------------------------------------------------
/**
 * One entry in the deduplicated buyer directory: a projection over every lead
 * (including archived), across every show and every user, so repeat-buyer
 * autofill recognises someone met at a different show on a different phone.
 * Precached; downloads first. See docs/plan.md, "The buyer directory has to be
 * precached too". Fields are lenient strings — this is a display projection,
 * not an entity.
 */
export const BuyerDirectoryEntry = z.object({
    company: z.string().min(1),
    contactName: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    country: z.string().nullable(),
    lastSeenAt: z.iso.datetime()
});
// ---------------------------------------------------------------------------
// Event tagging
// ---------------------------------------------------------------------------
/**
 * The set of products tagged to an event, for catalogue tag mode and the
 * picker's default event scope. A plain id list — the client already holds the
 * products.
 */
export const TaggedProductIds = z.object({
    eventId: Id,
    productIds: z.array(Id)
});
/**
 * Every event↔product tag pair across all events — the bulk feed the offline
 * catalogue mirror precaches so "filter the browse list by event" works with no
 * signal (a per-event fetch would not survive a dead zone). Full-replaced into
 * the Dexie `eventTags` table on each pull.
 */
export const AllEventTags = z.object({
    tags: z.array(z.object({ eventId: Id, productId: Id }))
});
/**
 * Multipart field names for a lead mutation. The lead JSON travels in
 * `payload`; the visiting-card images in `cardFront` / `cardBack`. Shared so
 * the API parser, the online edit path and the offline sync-engine replay all
 * agree on the exact strings (they must, or a card silently never arrives).
 */
export const LEAD_PAYLOAD_FIELD = 'payload';
export const CARD_FRONT_FIELD = 'cardFront';
export const CARD_BACK_FIELD = 'cardBack';
//# sourceMappingURL=api.js.map